import type { HotkeyBindingStatus } from '@/lib/platform/types';

/**
 * Hyprland keybind lines for the external-trigger path (TechSpec §10.3):
 * the compositor owns the key and runs `vision360 --toggle <mode>`, which
 * reaches the running instance over its single-instance socket.
 *
 * `Ctrl+Shift+Comma` → `bind = CTRL SHIFT, comma, exec, vision360 --toggle overlay`
 */

const MODIFIERS: Record<string, string> = {
  ctrl: 'CTRL',
  control: 'CTRL',
  commandorcontrol: 'CTRL',
  shift: 'SHIFT',
  alt: 'ALT',
  super: 'SUPER',
  meta: 'SUPER',
};

/** Browser/Tauri key codes → xkb keysym names Hyprland understands. */
function keysym(code: string): string {
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter?.[1]) return letter[1];
  const digit = /^Digit(\d)$/.exec(code);
  if (digit?.[1]) return digit[1];
  const named: Record<string, string> = {
    Comma: 'comma',
    Period: 'period',
    Space: 'space',
    Slash: 'slash',
    Semicolon: 'semicolon',
  };
  return named[code] ?? code.toLowerCase();
}

export function hyprlandLine(binding: string, mode: string, binary = 'vision360'): string {
  const parts = binding.split('+').map((p) => p.trim());
  const key = parts.pop() ?? '';
  const mods = parts.map((p) => MODIFIERS[p.toLowerCase()] ?? p.toUpperCase()).join(' ');
  return `bind = ${mods}, ${keysym(key)}, exec, ${binary} --toggle ${mode}`;
}

export function hyprlandSnippet(bindings: readonly HotkeyBindingStatus[]): string {
  return [
    '# VISION360 — ~/.config/hypr/hyprland.conf',
    ...bindings.map((b) => hyprlandLine(b.binding, b.mode)),
  ].join('\n');
}
